import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { IonModal, IonButton, IonContent, IonHeader, IonToolbar, IonInput, IonImg, IonGrid, IonRow, IonCol, IonSearchbar, IonIcon, IonFooter, IonSegment, IonSegmentButton, IonLabel, IonText } from '@ionic/react';
import { pencil, checkmark, thumbsUp, thumbsDown, openOutline, cameraOutline, logoYoutube } from 'ionicons/icons';
import './ImageRetrievalModal.css';
import { _dict } from './constants';
import { useGlobalStore } from './stateManagement/globalZustand';
import { useCacheStore } from './stateManagement/cacheZustand';
import { label2LabelToShow } from '../foo/cards';
import { useUIStore } from './stateManagement/UIZustand';
import { ImageSourceType, fetchImagesWithFallback } from '../foo/retrieval';
import { randomAliasLabel } from '../foo/grid';
import { getImageUrl, usePhotoGallery } from './usePhotoGallery';
import VideoPopover from './dialogs/VideoPopover';
import IROtherOptions from './IROtherOptions';

interface ImageRetrievalModalProps {
  label: string;
  isOpen: boolean;
  onClose: (label: string | null, img: string | null) => void;
  initialImage: string | null;
}

const _default_images: { [key: string]: string[] } =
{
  "ARASAAC": [],
  "Sclera": [],
  "Flickr30k": [],
  "Unsplash": []
};

const ImageRetrievalModal: React.FC<ImageRetrievalModalProps> = ({ label, isOpen, onClose, initialImage }) => {
  const [mainImageIdx, setMainImageIdx] = useState<number>(-1);
  const [images, setImages] = useState<{ [key: string]: string[] }>(_default_images);
  const { lang } = useGlobalStore();
  const { retrievalCache, updateRetrievalCache } = useCacheStore();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [editableLabel, setEditableLabel] = useState<string>(label);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [visibleSourceMessage, setVisibleSourceMessage] = useState<string | null>(null);
  const { setIsSidebarVisible } = useUIStore();
  const [isOtherOptionsOpen, setIsOtherOptionsOpen] = useState<boolean>(false);


  const [showSources, setShowSources] = useState<boolean>(false);
  const [visibleSource, setVisibleSource] = useState<ImageSourceType | null>(null);
  const [searchButtonDisabled, setSearchButtonDisabled] = useState<boolean>(true);
  const [isVideoPopoverOpen, setIsVideoPopoverOpen] = useState<boolean>(false);

  const { takePhoto, photo } = usePhotoGallery();

  const searchInputRef = useRef<HTMLIonSearchbarElement>(null);


  const editInputRef = useRef<HTMLIonInputElement>(null);

  // AUTO-FOCUS PART
  useEffect(() => {
    if (editMode) {
      setTimeout(() => {
        editInputRef.current?.setFocus();
      }, 500);
    }
  }, [editMode]);

  /*useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.setFocus();
      }, 500);
    }
  }, [isOpen]);*/

  function handleSearchTermChange(event: any): void {
    setSearchTerm(event.detail.value || '');
    setSearchButtonDisabled(event.detail.value === '');
    setVisibleSource(null);
    setShowSources(false);
    setVisibleSourceMessage(null);
    setImages(_default_images);
  }


  useEffect(() => {
    const fetchData = async () => {
      if (searchTerm && searchTerm.length > 0 && visibleSource !== null && showSources === true) {
        setVisibleSourceMessage(_dict.loading[lang]);
        await fetchImages(searchTerm);
        setVisibleSourceMessage(null);
      }
    };
    fetchData();
  }, [showSources, visibleSource, searchTerm]);

  const fetchImages = useCallback(async (query: string) => {
    if (visibleSource === null) return;
    if (retrievalCache[query] && retrievalCache[query][visibleSource] && retrievalCache[query][visibleSource].data.length > 0) {
      setImages({ ...images, [visibleSource]: retrievalCache[query][visibleSource].data });
      return;
    }

    try {

      const response = await fetchImagesWithFallback(lang, query, 10, visibleSource, true, true)
      // available options: many, base64
      // const response = await fetch(`${_SERVER_URL_RP}/getimg/many-base64/${lang}/${query}`);
      if (response === null) {
        setMessage(_dict.connectionProblemsTryLater[lang]);
        return;
      }

      // Update images
      setImages({ ...images, [visibleSource]: response });

      // Update cache
      updateRetrievalCache(query, visibleSource, response);

    } catch (error) {
      console.error("WTF", error);
      setMessage(_dict.connectionProblemsTryLater[lang]);
    }
  }, [retrievalCache, updateRetrievalCache, lang, isOpen, label, visibleSource]);



  const handleEditToggle = () => {
    setEditMode(!editMode);

  };

  const handleLabelChange = (event: CustomEvent) => {
    setEditableLabel(event.detail.value);
  };

  const handleLabelUpdate = () => {
    setEditMode(false);

  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLabelUpdate();
    }
  };

  const handleDismiss = () => {
    onClose(null, null);
  }

  const handleSearchButtonClick = () => {
    setShowSources(true);
    setMessage(_dict.chooseImageSource[lang]);


  }

  const showSideBarIcon = <IonButton
    expand='block'
    color="light"
    onClick={() => {
      //handleDismiss();
      setIsOtherOptionsOpen(true);
    }}
    style={{
      fontSize: '24px',
      padding: '10px 20px',
      //position: 'relative',
      zIndex: 9999,
      margin: 0,
    }}
  >
    <IonIcon icon={openOutline} />
    <IonText>{_dict.otherOptions[lang]}</IonText>
  </IonButton>

  const handleSegmentChange = (e: CustomEvent) => {
    e.stopPropagation(); // This stops the event from propagating further
    setVisibleSource(e.detail.value);
    setMessage("");


  }
  
  const handleConfirmIR = () => {
    const lbl = randomAliasLabel(editableLabel);
    if (mainImageIdx === -1) {
      onClose(lbl, null);
      return;
    };
    if (mainImageIdx === -2 && photo !== undefined && photo !== null && photo.length > 0) {
      onClose(lbl, photo);
      return;
    } else if (visibleSource !== null && images[visibleSource].length > 0) {
      onClose(lbl, images[visibleSource].length > 0 ? images[visibleSource][mainImageIdx] : "");
    }
    return;
  }



  function handleImageClick(currentIdx: number): void {
    if (currentIdx === mainImageIdx) setMainImageIdx(-1);
    else setMainImageIdx(currentIdx);



  }

  useEffect(() => {
    if (photo !== undefined) {
      setMainImageIdx(-1);


    }
  }, [photo]);

  const handleVideoPopoverConfirm = (url: string | null) => {
    setIsVideoPopoverOpen(false);
    if (url === null) return;
    onClose(label, url);
  };

  const initialImageURL = useMemo(() => getImageUrl(initialImage), [initialImage]);
  const photoURL = useMemo(() => getImageUrl(photo), [photo]);

  return (
    <>
    <IonModal isOpen={isOpen} onDidDismiss={handleDismiss}>
      <IonHeader>
        <IonToolbar >


          <div style={{
            display: 'flex', flexWrap: "wrap", justifyContent: "center",
            alignItems: 'center', flexGrow: 1, padding: "10px"
          }}>
            {editMode ? (
              <>
                <IonInput
                  value={label2LabelToShow(editableLabel, lang)}
                  onIonChange={handleLabelChange}
                  ref={editInputRef}
                  onKeyDown={handleKeyDown}
                  //autofocus
                  style={{
                    width: `fit-content`,
                    maxWidth: '90%', background: '#f0f0f0', padding: '5px', borderRadius: '5px',
                    textAlign: "center",
                    fontSize: "1.1em",
                  }}
                />
                <IonButton color="success" onClick={handleLabelUpdate} size='large'>
                  <IonIcon icon={checkmark} />
                </IonButton>


              </>
            ) : (
              <>
                <h2>{label2LabelToShow(editableLabel, lang)}</h2>
                <IonButton color="warning" onClick={handleEditToggle}
                  size='large'
                  style={{ margin: "10px" }}>
                  <IonIcon size="large" icon={pencil} />
                </IonButton>

              </>
            )}
            {showSideBarIcon}
          </div>


        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonGrid>
          <IonRow>
            {initialImageURL !== null && <IonCol size="6" offset={photo === null || photo === undefined ? "3" : "0"}>

              <div 
                className={`${mainImageIdx === -1 ? 'selectedImage' : ''}`}
                onClick={() => setMainImageIdx(-1)}>
                  
                <IonImg src={initialImageURL} style={{height:"100px"}} />
              </div>


            </IonCol>}
            {photoURL !== null && (
              <IonCol size="6">
                <div
                  className={`${mainImageIdx === -2 ? 'selectedImage' : ''}`}
                  onClick={() => setMainImageIdx(-2)}
                >
                  <IonImg src={photoURL} style={{height:"100px"}} />
                </div>
              </IonCol>
            )}

            <IonCol size="12">
              <div className="message">{_dict.loadFrom[lang]}</div>
            </IonCol>
            <IonCol size="6">
              <div className="actionButtons">
                <IonButton
                  size="large"
                  expand="block"
                  color="light"
                  className="fullWidthButton"
                  onClick={() => takePhoto(false)}
                >
                  <IonIcon slot="start" icon={cameraOutline} />
                  {_dict.thisDevice[lang]}
                </IonButton>
              </div>
            </IonCol>
            <IonCol size="6">
              <div className="actionButtons">
                <IonButton
                  size="large"
                  expand="block"
                  color="danger"
                  className="fullWidthButton"
                  onClick={() => setIsVideoPopoverOpen(true)} /* Assuming this is a placeholder for the correct action */
                >
                  <IonIcon slot="start" icon={logoYoutube} />
                  Youtube
                </IonButton>
              </div>
            </IonCol>
          </IonRow>
          <VideoPopover isOpen={isVideoPopoverOpen} onConfirm={handleVideoPopoverConfirm} lang={lang} />
        </IonGrid>
        <div className="message">{_dict.or[lang]}</div>
        <IonSearchbar
          ref={searchInputRef}
          value={searchTerm}
          onIonInput={handleSearchTermChange}
          placeholder={_dict.searchMoreImages[lang]}
          animated={true}
          showClearButton="always"
        ></IonSearchbar>
        <IonButton size="large" expand="full" color="light" onClick={handleSearchButtonClick}
          disabled={searchButtonDisabled}
        >
          {_dict.search[lang]}
        </IonButton>

        <div className="message">{message}</div>

        <IonGrid>
          <IonRow>
            <IonCol>
              {<IonSegment disabled={showSources === false} key={visibleSource} value={visibleSource === null ? undefined : visibleSource}
                onIonChange={handleSegmentChange}>
                <IonSegmentButton value="ARASAAC" >
                  <IonLabel>ARASAAC</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="Unsplash" >
                  <IonLabel>Unsplash</IonLabel>
                </IonSegmentButton>

                {/*<IonSegmentButton value="Flickr30k" >
            <IonLabel>Photo</IonLabel>
                    </IonSegmentButton>*/}
              </IonSegment>}
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol style={{ height: "20px" }}></IonCol>
          </IonRow>
          {visibleSource !== null && <IonRow>
            {visibleSourceMessage}

            {images[visibleSource].map((image, idx) => (
              <IonCol size="6" sizeSm="4" sizeMd="3" sizeLg="2" key={idx}>
                <div className={`image-container ${mainImageIdx === idx ? 'highlighted' : ''}`}>
                  <img
                    key={"img" + idx}
                    src={image}
                    onClick={() => handleImageClick(idx)}

                    style={mainImageIdx === idx ? {
                      border: '7px solid var(--ion-color-success)',
                      borderRadius: '4px',
                      transform: 'scale(1.1)',
                    } : {}}
                  />
                </div>

              </IonCol>

            ))}
          </IonRow>}
        </IonGrid>
      </IonContent>
      <IonFooter >
        <IonToolbar >
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", background: "var(--ion-color-dark)" }}>
            {editMode === false && <><IonButton color="success" size='large' onClick={handleConfirmIR} slot="end">
              {_dict.done[lang]}
              <IonIcon icon={thumbsUp} slot="icon-only" />
            </IonButton>
              <IonButton color="danger" size='large' onClick={handleDismiss} slot="end">
                {_dict.undo[lang]}
                <IonIcon icon={thumbsDown} slot="icon-only" />
              </IonButton></>
            }
          </div>
        </IonToolbar>
      </IonFooter>
    </IonModal>
    <IROtherOptions
    showModal={isOtherOptionsOpen}
    setShowModal={setIsOtherOptionsOpen}
    IRLabel={label}
    handleIRDismiss={handleDismiss} />
    </>
  );
};

export default ImageRetrievalModal;
